
import React, { FC, useEffect, useState } from "react";
import "./PurchasedItems.scss";
import PublicAppContainer from "app_components/layouts/public";
import TextComponent from "app_components/commons/TextComponent/TextComponent";
import ButtonComponent from "app_components/commons/Button/ButtonComponent";
import { ScreenNotificationsUtil } from "app_utils/screenNotification";
import PurchasedItemsService, { IUserFormData } from "app_services/purchasedItems/purchasedItemsService";
import { IPurchasedPlace, PurchasedPlaceStatus } from "app_types/purchasedItems/purchasedItemsTypes";
import { PROCESS_ITEM_PURCHASE, PROCESS_PURCHASE } from "app_types/shoppingCart/shoppingCarTypes";
import { FaExclamationTriangle, FaInfoCircle, FaSearch, FaEdit, FaUser } from "react-icons/fa";
import { store } from 'app_storage/redux-storage';

const emptyStateImage = require("app_assets/images/resources/shopping_cart_empty.png");

interface IPurchasedItemProps {}

interface IGroupedPurchasedPlace {
  serviceId: string;
  programName: string;
  programCode: string;
  totalPlaces: number;
  assignedPlaces: number;
  availablePlaces: number;
  places: IPurchasedPlace[];
  purchacePlaceIds: string[];
  coverUrl: string;
}

interface IValidationMessage {
  type: 'warning' | 'restricted';
  message: string;
}

enum SearchState {
  INITIAL = 'initial',
  SEARCHING = 'searching',
  USER_FOUND = 'user_found',
  USER_NOT_FOUND = 'user_not_found',
  FOR_MYSELF = 'for_myself'
}

const PurchasedItems: FC<IPurchasedItemProps> = () => {
  const [purchasedPlaces, setPurchasedPlaces] = useState<IPurchasedPlace[]>([]);
  const [groupedPlaces, setGroupedPlaces] = useState<IGroupedPurchasedPlace[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showLicensesModal, setShowLicensesModal] = useState<boolean>(false);
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<IGroupedPurchasedPlace | null>(null);
  const [formData, setFormData] = useState<IUserFormData>({
    firstName: "",
    lastName: "",
    docNumber: "",
    docType: "CC",
    email: "",
    forMyself: false
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [assigningPlace, setAssigningPlace] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<IValidationMessage | null>(null);
  const [forceAssign, setForceAssign] = useState<boolean>(false);
  const [searchState, setSearchState] = useState<SearchState>(SearchState.INITIAL);
  const [searchDocNumber, setSearchDocNumber] = useState<string>("");

  const purchasedItemsService = new PurchasedItemsService();
  const screenNotifications = new ScreenNotificationsUtil();

  useEffect(() => {
    fetchPurchasedPlaces();
  }, []);

  useEffect(() => {
    if (purchasedPlaces.length > 0) {
      groupPlacesByScheduling();
    }
  }, [purchasedPlaces]);

  const fetchPurchasedPlaces = async () => {
    try {
      setLoading(true);
      const response = await purchasedItemsService.getPurchasedPlaces();

      if (response.status === "success" && response.purchasedPlaces) {
        setPurchasedPlaces(response.purchasedPlaces);
      } else {
        screenNotifications.fireToast(
          {
            status: "error",
            message: response.message || "Error al cargar los productos adquiridos",
          },
          {
            position: "bottom-right",
          }
        );
      }
    } catch (error) {
      console.error("Error fetching purchased places:", error);
      screenNotifications.fireToast(
        {
          status: "error",
          message: "Error al cargar los productos adquiridos",
        },
        {
          position: "bottom-right",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const groupPlacesByScheduling = () => {
    const groupedData: { [key: string]: IGroupedPurchasedPlace } = {};

    purchasedPlaces.forEach(place => {
      const serviceId = place.courseScheduling.metadata.service_id;

      if (!groupedData[serviceId]) {
        groupedData[serviceId] = {
          serviceId: serviceId,
          programName: place.programName,
          programCode: place.programCode,
          totalPlaces: 0,
          assignedPlaces: 0,
          availablePlaces: 0,
          places: [],
          coverUrl: place.extra_info?.coverUrl || "",
          purchacePlaceIds: []
        };
      }

      groupedData[serviceId].totalPlaces += 1;

      if (place.status === PurchasedPlaceStatus.ASSIGNED) {
        groupedData[serviceId].assignedPlaces += 1;
      } else if (place.status === PurchasedPlaceStatus.AVAILABLE) {
        groupedData[serviceId].availablePlaces += 1;
      }

      groupedData[serviceId].places.push(place);
      groupedData[serviceId].purchacePlaceIds.push(place._id);
    });

    setGroupedPlaces(Object.values(groupedData));
  };

  const handleOpenAssignModal = (group: IGroupedPurchasedPlace) => {
    setSelectedGroup(group);
    setShowAssignModal(true);
    resetForm();
  };

  const handleOpenLicensesModal = (group: IGroupedPurchasedPlace) => {
    setSelectedGroup(group);
    setShowLicensesModal(true);
  };

  const handleCloseModal = () => {
    setShowAssignModal(false);
    setShowLicensesModal(false);
    setSelectedGroup(null);
    resetForm();
    setValidationMessage(null);
    setForceAssign(false);
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      docNumber: "",
      docType: "CC",
      email: "",
      forMyself: false
    });
    setFormErrors({});
    setValidationMessage(null);
    setForceAssign(false);
    setSearchState(SearchState.INITIAL);
    setSearchDocNumber("");
  };

  const resetSearch = () => {
    setSearchState(SearchState.INITIAL);
    setSearchDocNumber("");
    setFormData({
      firstName: "",
      lastName: "",
      docNumber: "",
      docType: "CC",
      email: "",
      forMyself: false
    });
    setFormErrors({});
    setValidationMessage(null);
    setForceAssign(false);
  };

  const handleSearchDocNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchDocNumber(e.target.value);
    // Limpiar errores si existe
    if (formErrors.docNumber) {
      setFormErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.docNumber;
        return newErrors;
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar error si existe
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }

    // Limpiar mensaje de validación si se cambia algún dato
    if (validationMessage) {
      setValidationMessage(null);
      setForceAssign(false);
    }
  };

  const handleForMyself = () => {
    const currentUser = store.getState().auth?.user;

    if (!currentUser) {
      screenNotifications.fireToast(
        {
          status: "error",
          message: "No se pudo obtener la información del usuario actual",
        },
        {
          position: "bottom-right",
        }
      );
      return;
    }

    setSearchState(SearchState.FOR_MYSELF);
    setFormData({
      firstName: currentUser.data?.profile?.first_name || "",
      lastName: currentUser.data?.profile?.last_name || "",
      docNumber: currentUser.data?.profile?.doc_number || "",
      docType: currentUser.data?.profile?.doc_type || "CC",
      email: currentUser.data?.email || "",
      forMyself: true
    });
    setSearchDocNumber(currentUser.data?.profile?.doc_number || "");
  };

  const handleSearchUser = async () => {
    if (!searchDocNumber.trim()) {
      setFormErrors(prev => ({
        ...prev,
        docNumber: "El número de documento es requerido"
      }));
      return;
    }

    try {
      setSearchState(SearchState.SEARCHING);
      const response = await purchasedItemsService.findUserByDocument(searchDocNumber);

      if (!response) {
        setSearchState(SearchState.INITIAL);
        return; // El servicio ya muestra un mensaje de error
      }

      if (response.status === 'success' && response.userData) {
        // Usuario encontrado, popular los campos del formulario
        setFormData({
          firstName: response.userData.firstName || "",
          lastName: response.userData.lastName || "",
          docNumber: searchDocNumber,
          docType: response.userData.docType || "CC",
          email: response.userData.email || "",
          forMyself: false
        });
        setSearchState(SearchState.USER_FOUND);
      } else {
        // Usuario no encontrado, permitir llenar datos manualmente
        setFormData({
          firstName: "",
          lastName: "",
          docNumber: searchDocNumber,
          docType: "CC",
          email: "",
          forMyself: false
        });
        setSearchState(SearchState.USER_NOT_FOUND);
        screenNotifications.fireToast(
          {
            status: "info",
            message: "Usuario no encontrado. Por favor, complete los datos manualmente.",
          },
          {
            position: "bottom-right",
          }
        );
      }
    } catch (error) {
      console.error("Error searching user:", error);
      setSearchState(SearchState.INITIAL);
      screenNotifications.fireToast(
        {
          status: "error",
          message: "Error al buscar el usuario",
        },
        {
          position: "bottom-right",
        }
      );
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!formData?.firstName?.trim()) {
      errors.firstName = "El nombre es requerido";
    }

    if (!formData?.lastName?.trim()) {
      errors.lastName = "El apellido es requerido";
    }

    if (!formData?.docNumber?.trim()) {
      errors.docNumber = "El número de documento es requerido";
    }

    if (!formData?.docType) {
      errors.docType = "El tipo de documento es requerido";
    }

    if (!formData?.email?.trim()) {
      errors.email = "El correo electrónico es requerido";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Ingrese un correo electrónico válido";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAssignPlace = async (_forceAssign = false) => {
    if (!selectedGroup) return;

    if (!validateForm()) {
      return;
    }

    try {
      setAssigningPlace(true);

      // Encontrar el primer lugar disponible para asignar
      const availablePlace = selectedGroup.places.find(place => place.status === PurchasedPlaceStatus.AVAILABLE);

      if (!availablePlace) {
        screenNotifications.fireToast(
          {
            status: "error",
            message: "No hay lugares disponibles para asignar",
          },
          {
            position: "bottom-right",
          }
        );
        return;
      }

      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        docNumber: formData.docNumber,
        docType: formData.docType,
        email: formData.email,
        forceAssign: _forceAssign,
        forMyself: formData.forMyself
      };

      screenNotifications.fireProcessing({
        title: 'Procesando inscripción...',
        text: 'Por favor, espera mientras procesamos la inscripción.'
      });

      const response = await purchasedItemsService.assignPlace(availablePlace._id, userData);

      // Cerrar el diálogo de procesamiento
      screenNotifications.close();

      if (!response) {
        return; // El servicio ya muestra un mensaje de error
      }

      if (response.status === 'error') {
        screenNotifications.fireToast(
          {
            status: 'error',
            message: response.message || 'Ha ocurrido un error al procesar la inscripción.'
          },
          {
            position: "bottom-right",
          }
        );
        return;
      }

      if (response.status === "success") {
        if (response.processPurchase === PROCESS_PURCHASE.VERIFY_PURCHASE && response.review) {
          const reviewItem:any = Object.values(response.review)[0];

          if (reviewItem) {
            if (reviewItem.processPurchase === PROCESS_ITEM_PURCHASE.RESTRICTED) {
              // Mostrar mensaje de restricción en la modal
              setValidationMessage({
                type: 'restricted',
                message: reviewItem.reason || 'No es posible asignar este curso a este usuario.'
              });
            } else if (reviewItem.processPurchase === PROCESS_ITEM_PURCHASE.WARNING) {
              // Mostrar mensaje de advertencia en la modal
              setValidationMessage({
                type: 'warning',
                message: reviewItem.reason || 'Hay una advertencia para esta asignación.'
              });
            }
          }
        } else {
          // Asignación exitosa
          screenNotifications.fireToast(
            {
              status: "success",
              message: "Producto asignado correctamente",
            },
            {
              position: "bottom-right",
            }
          );
          fetchPurchasedPlaces();
          handleCloseModal();
        }
      }
    } catch (error) {
      screenNotifications.close();
      console.error("Error assigning place:", error);
      screenNotifications.fireToast(
        {
          status: "error",
          message: "Error al asignar el producto",
        },
        {
          position: "bottom-right",
        }
      );
    } finally {
      setAssigningPlace(false);
    }
  };

  const handleForceAssign = () => {
    setForceAssign(true);
    handleAssignPlace(true);
  };

  const renderStatusBadge = (status: PurchasedPlaceStatus) => {
    let badgeClass = "";
    let statusText = "";

    switch (status) {
      case PurchasedPlaceStatus.AVAILABLE:
        badgeClass = "badge-warning";
        statusText = "Por inscribir";
        break;
      case PurchasedPlaceStatus.ASSIGNED:
        badgeClass = "badge-success";
        statusText = "Inscrito";
        break;
      case PurchasedPlaceStatus.EXPIRED:
        badgeClass = "badge-danger";
        statusText = "Expirado";
        break;
      case PurchasedPlaceStatus.CANCELLED:
        badgeClass = "badge-secondary";
        statusText = "Cancelado";
        break;
      default:
        badgeClass = "badge-info";
        statusText = status;
    }

    return <span className={`badge ${badgeClass}`}>{statusText}</span>;
  };

  const renderEmptyState = () => (
    <div className="purchased-items-empty">
      <img src={emptyStateImage} alt="No hay productos" />
      <TextComponent type="title">
        No tienes productos por inscribir
      </TextComponent>
      <p>Cuando compres cursos para ti o para otros, aparecerán aquí.</p>
    </div>
  );

  const renderValidationMessage = () => {
    if (!validationMessage) return null;

    return (
      <div className={`validation-message ${validationMessage.type}`}>
        {validationMessage.type === 'restricted' ? (
          <FaExclamationTriangle className="validation-icon" />
        ) : (
          <FaInfoCircle className="validation-icon" />
        )}
        <div>
          <p>{validationMessage.message}</p>
          {validationMessage.type === 'restricted' && (
            <ButtonComponent
              variant="secondary"
              // size="small"
              onClick={resetSearch}
              style={{ marginTop: '0.5rem' }}
            >
              <FaEdit style={{ marginRight: '0.5rem' }} />
              Cambiar usuario
            </ButtonComponent>
          )}
        </div>
      </div>
    );
  };

  const renderSearchSection = () => {
    if (searchState === SearchState.USER_FOUND || searchState === SearchState.USER_NOT_FOUND || searchState === SearchState.FOR_MYSELF) {
      return (
        <div className="search-user-section">
          <div className="search-result-header">
            {searchState === SearchState.FOR_MYSELF ? (
              <div className="user-found-message">
                <FaUser className="info-icon" />
                <p>Inscripción para ti mismo</p>
              </div>
            ) : searchState === SearchState.USER_FOUND ? (
              <div className="user-found-message">
                <FaInfoCircle className="info-icon" />
                <p>Usuario encontrado. Los datos han sido completados automáticamente.</p>
              </div>
            ) : (
              <div className="user-not-found-message">
                <FaInfoCircle className="info-icon" />
                <p>Usuario no encontrado. Complete los datos manualmente.</p>
              </div>
            )}
            <ButtonComponent
              variant="secondary"
              // size="small"
              onClick={resetSearch}
            >
              <FaEdit style={{ marginRight: '0.5rem' }} />
              Cambiar usuario
            </ButtonComponent>
          </div>
        </div>
      );
    }

    return (
      <div className="search-user-section">
        <div className="search-options">
          <ButtonComponent
            variant="secondary"
            onClick={handleForMyself}
            className="for-myself-button"
          >
            <FaUser style={{ marginRight: '0.5rem' }} />
            Para mí mismo
          </ButtonComponent>
          <div className="or-divider">
            <span>o</span>
          </div>
        </div>

        <div className="form-group search-group">
          <label htmlFor="searchDocNumber">Buscar por número de documento*</label>
          <div className="search-input-container">
            <input
              id="searchDocNumber"
              name="searchDocNumber"
              type="text"
              placeholder="Ingrese el número de documento"
              value={searchDocNumber}
              onChange={handleSearchDocNumberChange}
              className={formErrors.docNumber ? "input-error" : ""}
              disabled={searchState === SearchState.SEARCHING}
            />
            <ButtonComponent
              variant="primary"
              onClick={handleSearchUser}
              disabled={searchState === SearchState.SEARCHING || !searchDocNumber.trim()}
              className="search-button"
            >
              {searchState === SearchState.SEARCHING ? "Buscando..." : <FaSearch />}
            </ButtonComponent>
          </div>
          {formErrors.docNumber && <p className="error-message">{formErrors.docNumber}</p>}
        </div>
      </div>
    );
  };

  const renderUserForm = () => {
    const isFormDisabled = searchState === SearchState.INITIAL || searchState === SearchState.SEARCHING;
    const areFieldsReadOnly = searchState === SearchState.USER_FOUND || searchState === SearchState.FOR_MYSELF;

    if (isFormDisabled) {
      return (
        <div className="form-disabled-message">
          <p>Primero debe buscar un usuario o seleccionar "Para mí mismo" para habilitar el formulario.</p>
        </div>
      );
    }

    return (
      <div className="purchased-item-form-container">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">Nombre*</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              placeholder="Ingrese el nombre"
              value={formData.firstName}
              onChange={handleInputChange}
              className={formErrors.firstName ? "input-error" : ""}
              disabled={areFieldsReadOnly}
            />
            {formErrors.firstName && <p className="error-message">{formErrors.firstName}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Apellido*</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              placeholder="Ingrese el apellido"
              value={formData.lastName}
              onChange={handleInputChange}
              className={formErrors.lastName ? "input-error" : ""}
              disabled={areFieldsReadOnly}
            />
            {formErrors.lastName && <p className="error-message">{formErrors.lastName}</p>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="docType">Tipo de documento*</label>
            <select
              id="docType"
              name="docType"
              value={formData.docType}
              onChange={handleInputChange}
              className={formErrors.docType ? "input-error" : ""}
              disabled={areFieldsReadOnly}
            >
              <option value="CC">Cédula de Ciudadanía</option>
              <option value="CE">Cédula de Extranjería</option>
              <option value="TI">Tarjeta de Identidad</option>
              <option value="PP">Pasaporte</option>
            </select>
            {formErrors.docType && <p className="error-message">{formErrors.docType}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="docNumber">Número de documento*</label>
            <input
              id="docNumber"
              name="docNumber"
              type="text"
              placeholder="Número de documento"
              value={formData.docNumber}
              onChange={handleInputChange}
              className={formErrors.docNumber ? "input-error" : ""}
              disabled={true} // Siempre deshabilitado porque viene de la búsqueda
            />
            {formErrors.docNumber && <p className="error-message">{formErrors.docNumber}</p>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Correo electrónico*</label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="Ingrese el correo electrónico"
            value={formData.email}
            onChange={handleInputChange}
            className={formErrors.email ? "input-error" : ""}
            disabled={areFieldsReadOnly}
          />
          {formErrors.email && <p className="error-message">{formErrors.email}</p>}
        </div>
      </div>
    );
  };

  const renderAssignModal = () => {
    if (!showAssignModal || !selectedGroup) return null;

    const canSubmit = searchState !== SearchState.INITIAL && searchState !== SearchState.SEARCHING;

    return (
      <div className="modal_purchased_item modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <TextComponent type="subtitle">Inscribir a {selectedGroup.programName}</TextComponent>
            <button className="close-button" onClick={handleCloseModal}>×</button>
          </div>
          <div className="modal-body">
            <p>Tienes {selectedGroup.availablePlaces} cupos disponibles para asignar.</p>

            {renderValidationMessage()}
            {renderSearchSection()}
            {renderUserForm()}
          </div>
          <div className="modal-footer">
            <ButtonComponent
              variant="secondary"
              onClick={handleCloseModal}
              disabled={assigningPlace}
            >
              Cancelar
            </ButtonComponent>
            {validationMessage && validationMessage.type === 'warning' ? (
              <ButtonComponent
                variant="primary"
                onClick={handleForceAssign}
                disabled={assigningPlace || !canSubmit}
              >
                {assigningPlace ? "Asignando..." : "Confirmar asignación"}
              </ButtonComponent>
            ) : (
              <ButtonComponent
                variant="primary"
                onClick={() => handleAssignPlace(false)}
                disabled={assigningPlace || !canSubmit || (validationMessage?.type === 'restricted')}
              >
                {assigningPlace ? "Asignando..." : "Asignar"}
              </ButtonComponent>
            )}
          </div>
        </div>
      </div>
    );
  };


  const renderLicensesModal = () => {
    if (!showLicensesModal || !selectedGroup) return null;

    return (
      <div className="modal_purchased_item modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <TextComponent type="subtitle">Licencias de {selectedGroup.programName}</TextComponent>
            <button className="close-button" onClick={handleCloseModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="licenses-list">
              {selectedGroup.places.map((place) => (
                <div key={place.transaction} className="license-item">
                  <div className="license-info">
                    <p className="license-code">Código: {place.programCode}</p>
                    <div className="license-status">
                      {renderStatusBadge(place.status)}
                    </div>
                  </div>
                  {place.status === PurchasedPlaceStatus.ASSIGNED && place.assignedTo && (
                    <div className="license-details">
                      <p>Asignado a: {place.assignedTo.profile.first_name} {place.assignedTo.profile.last_name} ({place.assignedTo.email})</p>
                      {place.assignedAt && (
                        <p>Fecha de asignación: {new Date(place.assignedAt).toLocaleDateString()}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <ButtonComponent
              variant="secondary"
              onClick={handleCloseModal}
            >
              Cerrar
            </ButtonComponent>
          </div>
        </div>
      </div>
    );
  };

  const renderGroupedPlaces = () => {
    return (
      <div className="purchased-items-list">
        <div className="purchased-items-cards">
          {groupedPlaces.map((group) => (
            <div key={group.serviceId} className="purchased-item-card">
              <div className="purchased-item-image">
                <img
                  src={group.coverUrl}
                  alt={group.programName}
                />
              </div>
              <div className="purchased-item-info">
                <div className="purchased-item-title">
                  <TextComponent type="subtitle" className="program-name">
                    {group.programName}
                  </TextComponent>
                </div>
                <div className="purchased-item-details">
                  <div className="detail-item">
                    <span className="detail-label">Cupos adquiridos:</span>
                    <span className="detail-value">{group.totalPlaces}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Asignados:</span>
                    <span className="detail-value">{group.assignedPlaces}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Disponibles:</span>
                    <span className="detail-value">{group.availablePlaces}</span>
                  </div>
                </div>
              </div>
              <div className="purchased-item-action">
                {group.availablePlaces > 0 && (
                  <ButtonComponent
                    variant="primary"
                    onClick={() => handleOpenAssignModal(group)}
                    className="action-button"
                  >
                    Inscribir
                  </ButtonComponent>
                )}
                {group.assignedPlaces > 0 && (
                  <ButtonComponent
                    variant="secondary"
                    onClick={() => handleOpenLicensesModal(group)}
                    className="action-button"
                  >
                    Ver licencias
                  </ButtonComponent>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <PublicAppContainer>
      <div className="purchased-items-container">
        <TextComponent type="title" as="h2" className="page-title">
          Mis Productos adquiridos
        </TextComponent>

        {loading ? (
          <div className="purchased-items-loading">
            <p>Cargando productos...</p>
          </div>
        ) : purchasedPlaces.length === 0 ? (
          renderEmptyState()
        ) : (
          renderGroupedPlaces()
        )}

        {renderAssignModal()}
        {renderLicensesModal()}
      </div>
    </PublicAppContainer>
  );
};

export default PurchasedItems;